import { useCallback, useEffect, useRef, useState } from 'react';

import { preloadAllAssets } from '@/utils/asset-manifest';

export type AssetPreloadStatus = 'loading' | 'ready' | 'offline';

interface UseAssetPreloadReturn {
  status: AssetPreloadStatus;
  retry: () => void;
}

/**
 * Preloads all game assets on mount so gameplay + result screens work offline.
 * Returns a status that gates the UI: the app shell should only render once
 * status === "ready".
 */
export function useAssetPreload(): UseAssetPreloadReturn {
  const [status, setStatus] = useState<AssetPreloadStatus>(() =>
    typeof window === 'undefined' || window.navigator.onLine ? 'loading' : 'offline',
  );
  const settledRef = useRef(false);

  useEffect(() => {
    if (status === 'offline') return;

    settledRef.current = false;

    const handleOffline = () => {
      if (settledRef.current) return;
      settledRef.current = true;
      setStatus('offline');
    };
    window.addEventListener('offline', handleOffline);

    preloadAllAssets()
      .then(() => {
        if (settledRef.current) return;
        settledRef.current = true;
        setStatus('ready');
      })
      .catch(() => {
        if (settledRef.current) return;
        settledRef.current = true;
        setStatus('offline');
      });

    return () => {
      window.removeEventListener('offline', handleOffline);
    };
    // Runs once on mount; re-running when `status` flips to 'ready'/'offline' would re-trigger preload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = useCallback(() => {
    window.location.reload();
  }, []);

  return { status, retry };
}
